import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  Select,
  styled,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { AugmentedResponseWithSources } from '../../../../components/DealGPT/SourcesDisplay';
import { FeedbackContext } from '../../../../constants/feedbackConstants';
import { useFetchData } from '../../../../hooks/useFetchData';
import useQuestionStatusInfo from '../../../../hooks/useQuestionStatusInfo';
import { selectFolders } from '../../../../redux/reducers/folderReducer';
import {
  deleteProjectKPI,
  processQuestion,
  updateProjectKpiStatus,
  userEditSave,
} from '../../../../services/dealGPT';
import { DynamicText } from '../../../../theme/CustomComponents';
import type { ProjectRunAnalysisQuestion } from '../../../../Types/dealGPT';
import {
  AnalysisQuestionStatus,
  KpiSource,
  ProcessReason,
  UpdatedReason,
} from '../../../../Types/enums';
import { FeedbackInterface } from '../../../../Types/feedback';
import { formatDate } from '../../../../util/dateUtils';
import { noop } from '../../../../util/defaults';
import { renderMessage } from '../../../../util/messageUtils';
import { kpiQuestionVersionsPathUrl } from '../../../../util/routesUrls';
import Feedback from '../../../Feedback';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useFeatures } from '../../../Providers/FeatureProvider';
import { useNotifications } from '../../../Providers/NotificationsProvider';
import QuestionStatus from '../../QuestionStatus';
import QuestionStatusIndicator from '../../QuestionStatusIndicator';
import MoreOptionsMenu from '../MoreOptionsMenu';
import ReadMoreText from '../ReadMoreText';
import ReferenceDocuments from '../ReferenceDocuments';
import SectionButtons from '../SectionButtons';
import UnprocessedChangesDot from '../UnprocessedChangesDot';

const KPIAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  paddingLeft: 0,
  paddingRight: 0,

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },

  '& .MuiAccordionSummary-content': {
    display: 'flex',
    maxWidth: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: '8px',
  },
});

const KPIAccordionDetails = styled(AccordionDetails)({
  paddingRight: 0,
  display: 'flex',
  flexDirection: 'row',
});

const ReactMarkdownWrapper = styled('div')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
});

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '10px',
    color: '#092B49',
  },
  width: '100%',
});

const KPIStatusLabels: Record<AnalysisQuestionStatus, string | undefined> = {
  [AnalysisQuestionStatus.NEEDS_CONFIRMATION]: 'Partial',
  [AnalysisQuestionStatus.NOT_FOUND]: 'Missing',
  [AnalysisQuestionStatus.CONFIRMED]: 'Confirmed',
  [AnalysisQuestionStatus.ANSWERED]: undefined,
  [AnalysisQuestionStatus.ERROR]: undefined,
  [AnalysisQuestionStatus.PENDING]: undefined,
  [AnalysisQuestionStatus.INITIAL]: undefined,
  [AnalysisQuestionStatus.INPUT_PENDING]: undefined,
};
interface KPIProps {
  kpi: ProjectRunAnalysisQuestion;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  refetchQuestions: () => void;
  expandAll: boolean;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
}

const KPI: React.FC<KPIProps> = ({
  kpi,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  refetchQuestions,
  expandAll,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
}) => {
  const [analysisQuestionData, setAnalysisQuestionData] = useState<ProjectRunAnalysisQuestion>(kpi);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [expandAccordion, setExpandAccordion] = useState<boolean>(expandAll);
  const [searchParams, setSearchParams] = useSearchParams();
  const analytics = useAnalytics();
  const features = useFeatures();
  const accordionRef = useRef<HTMLElement | null>(null);
  const { statusColor, statusExplanation } = useQuestionStatusInfo(
    analysisQuestionData.status,
    analysisQuestionData.value
  );
  const { refetchNotifications } = useNotifications();

  const { currentFolder } = useSelector(selectFolders);
  const projectId = currentFolder?.projectId;

  const { data: questionVersions } = useFetchData<ProjectRunAnalysisQuestion[]>({
    urlPath: kpiQuestionVersionsPathUrl(projectId ?? '', kpi.id),
    skip: !projectId || !expandAccordion,
  });

  const questionStatuses: string[] = [];

  if (analysisQuestionData.validationResponse) {
    questionStatuses.push('VALIDATED');
  }

  /** The label is determined by the Answer. If the answer is undefined, we check status Missing status for backward compatibility. */
  const AnswerLabel = useMemo(() => {
    return analysisQuestionData.value || KPIStatusLabels[AnalysisQuestionStatus.NOT_FOUND];
  }, [analysisQuestionData.value]);

  const handleConfirm = useCallback(async () => {
    if (!projectId) return;

    analytics.event(
      AnalyticsEvent.WM_IA_A_KPI_CONFIRM,
      {
        id: analysisQuestionData.id,
        value: `Project=${projectId},Run=${analysisQuestionData.runId},Scope=${analysisQuestionData.scope}`,
      },
      {
        projectId,
        runId: analysisQuestionData.runId,
        scope: analysisQuestionData.scope,
        questionId: analysisQuestionData.id,
      }
    );

    const confirmed = await updateProjectKpiStatus(
      projectId,
      analysisQuestionData,
      AnalysisQuestionStatus.CONFIRMED
    );

    setAnalysisQuestionData(confirmed);
    refetchQuestions();
    enqueueSnackbar('Response confirmed successfully', { variant: 'success' });
  }, [projectId, analysisQuestionData, refetchQuestions, analytics]);

  // This is used when the user clicks the save button
  const handleSave = useCallback(
    async (
      values: Record<string, string>,
      reason?: UpdatedReason,
      processReason?: ProcessReason
    ) => {
      if (reason === UpdatedReason.USER_EDIT) {
        try {
          const value = values.value;
          const response = values.response;

          const nothingChanged = value === kpi.value && response === kpi.response;
          if (nothingChanged) return;

          // If the user is saving a manual response, we auto confirm it
          const updatedResult = await userEditSave({
            ...analysisQuestionData,
            value,
            response,
            status: AnalysisQuestionStatus.CONFIRMED,
            updatedReason: reason,
          });

          if (updatedResult.id) {
            setAnalysisQuestionData(updatedResult);
            enqueueSnackbar('Content saved successfully', { variant: 'success' });
            refetchQuestions();
          } else {
            enqueueSnackbar('Failed to save content. Please try again later.', {
              variant: 'error',
            });
            return;
          }
        } catch (error) {
          enqueueSnackbar('Failed to save KPI. Please try again later.', {
            variant: 'error',
          });
        }
      }

      if (reason === UpdatedReason.REPROCESS || processReason === ProcessReason.APPLY_CHANGES) {
        try {
          enqueueSnackbar('Your content is being processed', { variant: 'info' });
          setIsLoadingThis(true);
          if (processReason === ProcessReason.APPLY_CHANGES) {
            setIsLoadingApplyChanges(true);
          }
          await processQuestion(analysisQuestionData, processReason);
          refetchNotifications();
          refetchQuestions();
        } catch {
          enqueueSnackbar('Failed to apply changes. Please try again later.', {
            variant: 'error',
          });
        } finally {
          setIsLoadingThis(false);
          setIsLoadingApplyChanges(false);
        }
      }
    },
    [
      kpi,
      analysisQuestionData,
      refetchQuestions,
      setIsLoadingThis,
      setIsLoadingApplyChanges,
      refetchNotifications,
    ]
  );

  const handleVersionChange = useCallback(
    (value: string) => {
      const version = questionVersions?.find((v) => v.id === value);
      if (version) {
        analytics.event(
          AnalyticsEvent.WM_IA_A_KPI_QUESTION_VERSION_CHANGE,
          {
            id: value,
            value: version.pageName,
          },
          {
            projectId,
            runId: version.runId,
            scope: version.scope,
            oldVersionId: analysisQuestionData.id,
            newVersionId: version.id,
          }
        );
        setAnalysisQuestionData(version);
      }
    },
    [questionVersions, analytics, projectId, analysisQuestionData.id]
  );

  const handleDelete = useCallback(async () => {
    if (!projectId) return;
    analytics.event(
      AnalyticsEvent.WM_IA_A_KPI_DELETE,
      {
        id: analysisQuestionData.id,
        value: `Project=${projectId},Run=${analysisQuestionData.runId},Scope=${analysisQuestionData.scope}`,
      },
      {
        projectId,
        runId: analysisQuestionData.runId,
        scope: analysisQuestionData.scope,
        questionId: analysisQuestionData.id,
      }
    );

    await deleteProjectKPI(projectId, analysisQuestionData.id);

    enqueueSnackbar('KPI deleted successfully', { variant: 'success' });
    refetchQuestions();
  }, [projectId, analysisQuestionData, refetchQuestions, analytics]);

  const toggleAccordion = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      analytics.event(
        AnalyticsEvent.WM_IA_A_KPI_QUESTION_ACCORDION,
        {
          id: analysisQuestionData.id,
          value: isExpanded.toString(),
        },
        {
          projectId,
          runId: analysisQuestionData.runId,
          scope: analysisQuestionData.scope,
          questionId: analysisQuestionData.id,
        }
      );
      setExpandAccordion(isExpanded);
    },
    [analysisQuestionData, analytics, projectId]
  );

  useEffect(() => {
    setExpandAccordion(expandAll);
  }, [expandAll]);

  // Update the question data when the kpi prop changes
  useEffect(() => setAnalysisQuestionData(kpi), [kpi]);

  // Expand the accordion and auto scroll, if the questionId is in the URL
  useEffect(() => {
    const questionId = searchParams.get('questionId');

    if (questionId && questionId === analysisQuestionData.id) {
      setExpandAccordion(true);
      accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Remove "questionId" param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('questionId');
      setSearchParams(newParams);
    }
  }, [searchParams, analysisQuestionData.id, setSearchParams]);

  return (
    <Form
      onSubmit={noop}
      initialValues={{
        ['response']: analysisQuestionData.response,
        ['value']: analysisQuestionData.value,
      }}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <Accordion
            square
            disableGutters
            sx={{ boxShadow: 'none' }}
            expanded={expandAccordion}
            onChange={toggleAccordion}
          >
            <KPIAccordionSummary>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: '35px',
                  width: '3%',
                  alignItems: 'flex-start',
                }}
              >
                <QuestionStatusIndicator explanation={statusExplanation} color={statusColor} />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '40%',
                  paddingRight: '17px',
                }}
              >
                <Typography sx={{ fontWeight: '550', color: '#092B49' }}>
                  {analysisQuestionData.prettyQuestion}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: '13px' }}>
                  {analysisQuestionData.area} - {analysisQuestionData.dimension}
                </Typography>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', width: '52%' }}>
                {!expandAccordion ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '90%' }}>
                      <Typography
                        component="p"
                        sx={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          WebkitLineClamp: 2,
                          fontSize: '14px',
                          fontWeight: '400',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {analysisQuestionData && (
                          <DynamicText fontWeight={'500'}>{AnswerLabel}</DynamicText>
                        )}
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, supersub]}
                          components={{
                            sup: ({ children }) => (
                              <AugmentedResponseWithSources
                                text={children.toString()}
                                sources={analysisQuestionData.documents ?? []}
                              />
                            ),
                          }}
                        >
                          {renderMessage({
                            message: analysisQuestionData.response || '',
                            sources: analysisQuestionData.documents || [],
                          })}
                        </ReactMarkdown>
                      </Typography>
                    </div>
                    <div style={{ margin: 'auto' }}>
                      <QuestionStatus statuses={questionStatuses} />
                    </div>
                  </>
                ) : (
                  // Expanded view
                  <>
                    <QuestionStatus statuses={questionStatuses} />
                    <div
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'row',
                        gap: '16px',
                        paddingRight: '10px',
                      }}
                    >
                      <SectionButtons
                        projectId={projectId as string}
                        handleSave={handleSave}
                        values={values}
                        hasGenerated={true}
                        handleEdit={setIsEditing}
                        isEditing={isEditing}
                        question={analysisQuestionData}
                        showGenerateButton={false}
                        showConfirmButton={
                          analysisQuestionData.status ===
                            AnalysisQuestionStatus.NEEDS_CONFIRMATION ||
                          (analysisQuestionData.status === AnalysisQuestionStatus.ANSWERED &&
                            !!analysisQuestionData.value)
                        }
                        handleConfirm={handleConfirm}
                        showSaveButton={isEditing}
                      />
                    </div>
                  </>
                )}
              </div>
              <MoreOptionsMenu
                projectId={projectId as string}
                analysisQuestionData={analysisQuestionData}
                handleSave={handleSave}
                values={values}
                isEditing={isEditing}
                canDelete={true}
                handleDelete={analysisQuestionData.createdBy === null ? undefined : handleDelete} // Dont allow delete if the question was createdBy the system
                handleValidateFindingsClick={handleValidateFindingsClick}
              />
            </KPIAccordionSummary>
            <KPIAccordionDetails ref={accordionRef}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
                <div
                  style={{ display: 'flex', gap: '10px', alignItems: 'center', paddingTop: '1em' }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: statusColor,
                      fontSize: '13px',
                      fontWeight: '500',
                    }}
                  >
                    {statusExplanation}
                  </Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    paddingTop: '1em',
                    width: '90%',
                  }}
                >
                  <StyledSelect
                    value={
                      questionVersions?.some((q) => q.id === analysisQuestionData.id)
                        ? analysisQuestionData.id
                        : ''
                    }
                    onChange={(event) => handleVersionChange(event.target.value as string)}
                  >
                    {questionVersions?.map((version) => (
                      <MenuItem
                        key={version.id}
                        value={version.id}
                        selected={version.id === analysisQuestionData.id}
                      >
                        Generated on {version.createdAt && formatDate(version.createdAt)} |{' '}
                        {version.updatedReason}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {kpi.unprocessedDownstreamChanges && <UnprocessedChangesDot />}
                </div>
                <div style={{ marginTop: '1.3em', width: '90%' }}>
                  {analysisQuestionData.kpiSource !== KpiSource.Database && (
                    <ReferenceDocuments
                      questionId={analysisQuestionData.id}
                      documents={analysisQuestionData.documents}
                      showAnalysisText={true}
                    />
                  )}
                  {analysisQuestionData.validationSources &&
                    analysisQuestionData.validationSources.length > 0 && (
                      <div style={{ marginTop: '1.3em' }}>
                        <ReferenceDocuments
                          questionId={analysisQuestionData.id}
                          documents={analysisQuestionData.validationSources}
                          showReferenceTitle={false}
                          showAnalysisText={true}
                          showValidationTitle={true}
                        />
                      </div>
                    )}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '55%',
                  paddingRight: '10px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-start',
                    marginBottom: !isEditing ? '0' : '15px',
                    marginRight: !isEditing ? '10px' : '0',
                  }}
                >
                  {!isEditing ? (
                    <Typography sx={!analysisQuestionData.value ? { color: 'grey' } : {}}>
                      {analysisQuestionData.value ?? 'N/A'}
                    </Typography>
                  ) : analysisQuestionData.lookupList ? (
                    <StyledSelect
                      value={analysisQuestionData.value}
                      onChange={(event) => {
                        setAnalysisQuestionData({
                          ...analysisQuestionData,
                          value: event.target.value as string,
                        });
                      }}
                      onClick={(event) => event.stopPropagation()}
                      sx={{ minWidth: '160px' }}
                    >
                      {(JSON.parse(analysisQuestionData.lookupList ?? '') as string[]).map(
                        (value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        )
                      )}
                    </StyledSelect>
                  ) : (
                    <MuiTextField
                      size="small"
                      value={analysisQuestionData.value}
                      onChange={(event) => {
                        setAnalysisQuestionData({
                          ...analysisQuestionData,
                          value: event.target.value as string,
                        });
                      }}
                      onClick={(event) => event.stopPropagation()}
                      sx={{ maxWidth: '10rem', minWidth: '160px' }}
                    />
                  )}
                </div>
                {isEditing ? (
                  <TextField name="response" multiline />
                ) : (
                  <ReactMarkdownWrapper>
                    {analysisQuestionData.response && (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, supersub]}
                        components={{
                          sup: ({ children }) => (
                            <AugmentedResponseWithSources
                              text={children.toString()}
                              sources={analysisQuestionData.documents ?? []}
                            />
                          ),
                        }}
                      >
                        {renderMessage({
                          message: analysisQuestionData.response || '',
                          sources: analysisQuestionData.documents || [],
                        })}
                      </ReactMarkdown>
                    )}

                    {analysisQuestionData?.validationResponse && (
                      <>
                        <hr />
                        <Typography>Validation</Typography>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, supersub]}
                          components={{
                            sup: ({ children }) => (
                              <AugmentedResponseWithSources
                                text={children.toString()}
                                sources={analysisQuestionData.validationSources ?? []}
                              />
                            ),
                          }}
                        >
                          {renderMessage({
                            message: analysisQuestionData.validationResponse || '',
                            sources: analysisQuestionData.validationSources || [],
                          })}
                        </ReactMarkdown>
                      </>
                    )}
                  </ReactMarkdownWrapper>
                )}
                {features.deal_gpt_enriched_prompt_display && (
                  <>
                    <Typography sx={{ fontWeight: '700', color: '#092B49', marginTop: '10px' }}>
                      Enriched Prompt:
                    </Typography>
                    <div style={{ marginBottom: '15px' }}>
                      <ReadMoreText
                        text={analysisQuestionData.enrichedPrompt || ''}
                        maxLength={55}
                      />
                    </div>
                  </>
                )}
                <Feedback
                  initialFeedback={
                    analysisQuestionData.analysisQuestionFeedback as FeedbackInterface | undefined
                  }
                  context={FeedbackContext.ANALYSIS_QUESTION}
                  onFeedbackSubmit={(feedback) =>
                    handleFeedbackSubmit(feedback, analysisQuestionData.id)
                  }
                />
              </div>
            </KPIAccordionDetails>
          </Accordion>
        </form>
      )}
    />
  );
};

export default KPI;
